import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  IconButton,
  Collapse,
  TextField,
  Tooltip,
  ButtonBase,
} from '@mui/material';
import {
  IconMessagePlus,
  IconTrash,
  IconRestore,
  IconChevronDown,
  IconExternalLink,
} from '@tabler/icons-react';
import type { TaskStatus } from '@techgitter/contracts';
import type { LiveTask } from '../../types/taskList';
import { isTaskDone } from '../../utils/checklistEngine';
import { ActionPicker } from '../dropdown/ActionPicker';

interface TaskRowProps {
  task: LiveTask;
  sectionId: string;
  disabled: boolean;
  /** Options resolved from the live dropdown catalog at render time (falls
   *  back to the task's baked-in options). Keeps a dropdown task's options
   *  correct even if the catalog finished loading after the checklist built. */
  resolvedOptions?: string[];
  onSetStatus: (status: TaskStatus) => void;
  onSetNote: (note: string) => void;
  onSetOption: (option: string) => void;
  onRemove: () => void;
  onRestore: () => void;
}

/**
 * Returns the task URL only when it is a safe http(s) link — guards against
 * `javascript:` / `data:` schemes since the value is author-supplied text.
 */
const safeHttpUrl = (raw?: string): string | undefined => {
  if (!raw) return undefined;
  try {
    const u = new URL(raw.trim());
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Outcome dropdown for a dropdown task — a compact trigger that opens the
 * shared ActionPicker (unified dropdown system §10), rather than a native/
 * MUI select. A leading "— None —" clears the selection (reverts to not-done).
 */
const TaskOutcomeSelect: React.FC<{
  value?: string;
  options: string[];
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ value, options, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const pickerOptions = [
    { value: '', label: '— None —' },
    ...options.map((o) => ({ value: o, label: o })),
  ];

  return (
    <>
      <ButtonBase
        ref={triggerRef}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: 'space-between',
          minWidth: 150,
          maxWidth: 220,
          height: 30,
          px: 1,
          bgcolor: '#fff',
          border: '0.5px solid #d0dcea',
          borderRadius: '6px',
          fontSize: 13,
          color: value ? '#1a1a1a' : '#bbb',
          '&:hover': { borderColor: '#c5d9f0' },
          '&.Mui-focusVisible': {
            borderColor: '#4A90D9',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
          }}
        >
          {value || 'Select…'}
        </Box>
        <IconChevronDown size={12} stroke={1.5} style={{ color: '#888', flexShrink: 0 }} />
      </ButtonBase>
      {open && (
        <ActionPicker
          options={pickerOptions}
          currentValue={value ?? ''}
          onSelect={onChange}
          onClose={() => setOpen(false)}
          anchorRef={triggerRef}
        />
      )}
    </>
  );
};

/**
 * A single checklist task row. Left: the task title with an info toggle right
 * beside it (expands the description as a selectable, copy-friendly block —
 * technicians often copy commands out of it), plus a note toggle. Right: a
 * checkbox for standard tasks, or the outcome dropdown for dropdown tasks.
 * Done = checked / an option chosen — and, if the note is mandatory, a
 * non-empty note recorded.
 */
export const TaskRow: React.FC<TaskRowProps> = ({
  task,
  disabled,
  resolvedOptions,
  onSetStatus,
  onSetNote,
  onSetOption,
  onRemove,
  onRestore,
}) => {
  const [noteOpen, setNoteOpen] = useState(false);
  const [descOpen, setDescOpen] = useState(false);

  if (!task.isVisible) return null;

  if (task.isRemoved) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, opacity: 0.5 }}
      >
        <Typography variant="body2" sx={{ textDecoration: 'line-through', flex: 1 }}>
          {task.title}
        </Typography>
        <Tooltip title="Restore task">
          <span>
            <IconButton size="small" onClick={onRestore} disabled={disabled}>
              <IconRestore size={16} stroke={1.5} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  const linkUrl = safeHttpUrl(task.url);
  const isDropdown = !!task.dropdownId;
  const isCompleted = task.status === 'completed';
  const done = isTaskDone(task);
  const noteMissing = !!task.noteRequired && !task.note.trim();
  const showNote = noteOpen || !!task.note || !!task.noteRequired;

  return (
    // data-task-id: the anchor the mobile job view scrolls to (next open task).
    <Box sx={{ px: 2, py: 0.25 }} data-task-id={task.taskId}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Left: title + info icon, grouped so the icon sits beside the text */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              minWidth: 0,
              textDecoration: done ? 'line-through' : 'none',
              color: done ? 'text.disabled' : 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.title}
            {task.noteRequired && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                *
              </Box>
            )}
          </Typography>
          {task.description && (
            <Tooltip title={descOpen ? 'Hide details' : 'Show details'}>
              <IconButton
                size="small"
                onClick={() => setDescOpen((o) => !o)}
                color={descOpen ? 'primary' : 'default'}
                aria-label={descOpen ? 'Hide task details' : 'Show task details'}
                sx={{ p: 0.25, flexShrink: 0 }}
              >
                <IconChevronDown
                  size={15}
                  stroke={1.5}
                  style={{
                    transform: descOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          {linkUrl && (
            <Tooltip title={`Open link: ${linkUrl}`} arrow>
              <IconButton
                size="small"
                sx={{ p: 0.25, flexShrink: 0 }}
                onClick={() => window.open(linkUrl, '_blank', 'noopener,noreferrer')}
              >
                <IconExternalLink size={15} stroke={1.5} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Right: note toggle · checkbox/dropdown · delete */}
        {!task.noteRequired && (
          <Tooltip title={showNote ? 'Hide note' : 'Add note'}>
            <span>
              <IconButton
                size="small"
                onClick={() => setNoteOpen((o) => !o)}
                disabled={disabled}
                color={task.note ? 'primary' : 'default'}
                sx={{ p: 0.25 }}
              >
                <IconMessagePlus size={16} stroke={1.5} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {isDropdown ? (
          <TaskOutcomeSelect
            value={task.selectedOption}
            options={resolvedOptions ?? task.options ?? []}
            disabled={disabled}
            onChange={onSetOption}
          />
        ) : (
          <Checkbox
            checked={isCompleted}
            onChange={() => onSetStatus(isCompleted ? 'new' : 'completed')}
            disabled={disabled}
            size="small"
            sx={{ p: 0.5 }}
          />
        )}

        <Tooltip title="Remove task">
          <span>
            <IconButton size="small" onClick={onRemove} disabled={disabled} sx={{ p: 0.25 }}>
              <IconTrash size={16} stroke={1.5} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {task.description && (
        <Collapse in={descOpen}>
          <Box
            sx={{
              mt: 0.5,
              mb: 1,
              px: 1,
              py: 0.75,
              bgcolor: '#f5f8fc',
              border: '0.5px solid #e2e9f2',
              borderRadius: '6px',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                userSelect: 'text',
                color: 'text.secondary',
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              {task.description}
            </Typography>
          </Box>
        </Collapse>
      )}

      <Collapse in={showNote}>
        <Box sx={{ pb: 1, pt: 0.5 }}>
          <TextField
            size="small"
            label={task.noteRequired ? 'Note (required)' : 'Note'}
            required={!!task.noteRequired}
            error={noteMissing}
            helperText={noteMissing ? 'A note is required to complete this task' : undefined}
            multiline
            minRows={1}
            maxRows={3}
            value={task.note}
            onChange={(e) => onSetNote(e.target.value)}
            disabled={disabled}
            fullWidth
          />
        </Box>
      </Collapse>
    </Box>
  );
};
